import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { useAuthStore } from '../store/authStore';
import { useRealtimeStore } from '../store/realtimeStore';
import { useSidebar } from '../contexts/SidebarContext';
import { NotificationPanel } from './NotificationPanel';
import KpiTicker from './cc/KpiTicker';
import clsx from 'clsx';
import { format } from 'date-fns';

export default function TopBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { sosEvents, isConnected, notifications } = useRealtimeStore();
  const { isOpen: sidebarOpen, toggle: toggleSidebar, isMobile } = useSidebar();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const activeSOS = sosEvents.filter(e =>
    ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(e.status)
  ).length;
  const unreadNotifications = notifications.filter(n => !n.read).length;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-command-panel border-b border-command-border relative z-[9999]">
      <div className="h-14 lg:h-16 px-2 sm:px-4 lg:px-6 flex items-center gap-1.5 sm:gap-3">
        {/* Sidebar toggle — in flow, never overlaps the clock */}
        <button
          onClick={toggleSidebar}
          className="flex-shrink-0 p-2.5 rounded-lg hover:bg-command-border transition-colors touch-manipulation"
          title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
        >
          {sidebarOpen && !isMobile ? (
            <XMarkIcon className="w-5 h-5 text-gray-200" />
          ) : (
            <Bars3Icon className="w-5 h-5 text-gray-200" />
          )}
        </button>

        {/* Left: KPI ticker (desktop) + SOS/message counters */}
        <div className="flex items-center gap-1.5 sm:gap-3 flex-1 min-w-0">
          <div className="hidden lg:block">
            <KpiTicker />
          </div>

          {/* Active SOS */}
          {activeSOS > 0 && (
            <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 bg-red-500/20 border border-red-500/50 rounded-lg animate-pulse whitespace-nowrap">
              <span className="text-red-500 font-bold">{activeSOS}</span>
              <span className="text-red-400 text-sm hidden sm:inline">SOS ACTIVE</span>
              <span className="text-red-400 text-xs sm:hidden">SOS</span>
            </div>
          )}

          {/* Unread Messages */}
          {unreadNotifications > 0 && (
            <div className="hidden sm:flex items-center gap-1.5 px-2 sm:px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded-lg whitespace-nowrap">
              <span className="text-blue-400 font-bold">{unreadNotifications}</span>
              <span className="text-blue-400 text-sm hidden md:inline">
                NEW MESSAGE{unreadNotifications > 1 ? 'S' : ''}
              </span>
              <span className="text-blue-400 text-xs md:hidden">NEW</span>
            </div>
          )}
        </div>

        {/* Center: Time — absolutely centered on desktop, inline on mobile */}
        <div className="lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 text-center flex-shrink-0">
          <div className="font-display text-base sm:text-lg lg:text-xl text-command-accent tracking-wider whitespace-nowrap">
            {format(currentTime, 'HH:mm:ss')}
          </div>
          <div className="text-xs text-gray-500 font-tactical hidden md:block">
            {format(currentTime, 'EEEE, dd MMM yyyy').toUpperCase()}
          </div>
        </div>

        {/* Right: Connection, Notifications & User */}
        <div className="flex items-center gap-1.5 sm:gap-3 flex-1 justify-end min-w-0">
          {/* Connection indicator — text chip on ≥sm, dot on phones */}
          <div
            className={clsx(
              'hidden sm:block px-2 py-1 rounded text-xs font-tactical whitespace-nowrap',
              isConnected
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            )}
          >
            {isConnected ? 'LIVE' : 'OFFLINE'}
          </div>
          <div
            className={clsx(
              'sm:hidden w-2.5 h-2.5 rounded-full flex-shrink-0',
              isConnected ? 'bg-green-400' : 'bg-red-500 animate-pulse'
            )}
            title={isConnected ? 'LIVE' : 'OFFLINE'}
            aria-label={isConnected ? 'Connected' : 'Disconnected'}
          />

          {/* Notifications Panel (Messages & Alerts) */}
          <NotificationPanel />

          {/* User menu — icon-only below md */}
          <Menu as="div" className="relative z-[99999]">
            <Menu.Button
              className="flex items-center gap-2 px-2.5 sm:px-3 py-2.5 sm:py-2 rounded-lg hover:bg-command-border transition-colors touch-manipulation"
              aria-label="User menu"
            >
              <UserCircleIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-gray-200">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500">{user?.role?.replace(/_/g, ' ').replace(/\s+User$/i, '')}</p>
              </div>
            </Menu.Button>

            <Transition
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className="absolute right-0 mt-2 w-56 bg-command-panel border border-command-border rounded-lg shadow-lg py-1"
                style={{ zIndex: 99999, position: 'absolute' }}
              >
                {/* Identity header for small screens where the name is hidden */}
                <div className="md:hidden px-4 py-2 border-b border-command-border">
                  <p className="text-sm font-medium text-gray-200 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.role?.replace(/_/g, ' ').replace(/\s+User$/i, '')}
                  </p>
                </div>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => navigate('/settings')}
                      className={clsx(
                        'flex items-center gap-2 w-full px-4 py-3 sm:py-2 text-sm touch-manipulation',
                        active ? 'bg-command-border text-white' : 'text-gray-400'
                      )}
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                      Settings
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={clsx(
                        'flex items-center gap-2 w-full px-4 py-3 sm:py-2 text-sm touch-manipulation',
                        active ? 'bg-command-border text-red-400' : 'text-gray-400'
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      Logout
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Mobile/tablet KPI strip — scrolls horizontally, never overflows the page */}
      <div className="lg:hidden border-t border-command-border/60 overflow-x-auto [-webkit-overflow-scrolling:touch]">
        <div className="w-max px-2 py-1">
          <KpiTicker />
        </div>
      </div>
    </header>
  );
}
